import { authActions, authGetters, authMutations, authState } from '@store/authStore';
import { chatActions, chatGetters, chatMutations, chatState } from '@store/chatStore';
import { globalActions, globalGetters, globalMutations, globalState } from '@store/globalStore';
import { groupActions, groupGetters, groupMutations, groupState } from '@store/groupStore';
import { indexActions, indexGetters, indexMutations, indexState } from '@store/indexStore';
import { resourceActions, resourceGetters, resourceMutations, resourceState } from '@store/resourceStore';
import { shareActions, shareGetters, shareMutations, shareState } from '@store/shareStore';
import { snackbarActions, snackbarGetters, snackbarMutations, snackbarState } from '@store/snackbarStore';
import { userActions, userGetters, userMutations, userState } from '@store/userStore';

import Resource from '@models/resource';
import User from '@models/user';
import Vue from 'vue';
import Vuex from "vuex";
import axios from 'axios';
import { baseURL } from 'config';

Vue.use(Vuex);

const state = () => {

  let state = {
    locales: ['en', 'fr'],
    locale: (typeof window === "undefined") ? 'en' : (window.navigator.userLanguage || window.navigator.language),
    selectedImages: [],
    allContacts: [],
    imgRecords: [],
    indexItems: [],
    indexItemsCount: 0,
    accessedPath: null,
    infiniteScrollEnabled: true,
    chatUnreadCount: 0,
    countryCode: null,
    myMessages: [],
    message: null,
    showLogin: null,
    resourceIndexState: 'idle',
    ...globalState,
    ...indexState,
    ...userState,
    ...resourceState,
    ...groupState,
    ...authState,
    ...chatState,
    ...snackbarState,
    ...shareState
  };

  return state;
};

const actions = {
  ...globalActions,
  ...indexActions,
  ...userActions,
  ...chatActions,
  ...groupActions,
  ...resourceActions,
  ...authActions,
  ...snackbarActions,
  ...shareActions,
  showLogin({ commit }, login) {
    commit('showLogin', login);
  },
  setInfiniteScroll({ commit }, bool) {
    commit("setInfiniteScroll", bool);
  },
  changeLocale({}, value) {
    localStorage.setItem("lang", value);
  },
  setIndexState: ({ commit }, { scrollTop }) => {
    commit("SET_RESOURCE_STATE", {
      scrollTop
    });
  },
  refreshIndexItems: ({ commit, dispatch }) => {
    commit("RESET_RESOURCE_AND_SCROLL_STATE");
    dispatch('getIndexItems');
  },
  clearFilters: ({ commit, dispatch }) => {
    commit("clearCategories");
    commit("clearGroups");
    dispatch('refreshIndexItems');
  },
  getIndexItems: async({ commit, getters, state }) => {

    if (state.resourceIndexState === 'loading' ||
      getters.indexPage > 1 && (getters.indexItemsCount === 0 ||
        getters.indexItemsCount === getters.indexItems.length)) {
      return;
    }

    commit("setInfiniteScroll", false);
    commit('setResourceIndexState', 'loading');



    const params = {};
    params.page = getters.indexPage;
    params.categories = state.indexCategoriesFilters;
    params.groups = state.indexGroupsFilters.map(g => g.id);
    try {
      const result = await axios.get('search', { params });
      const resources = result.data.resources;
      if (params.page === 1 && result.data.aggregations) {
        localStorage.setItem('categoriesCounter', JSON.stringify(result.data.aggregations.categories));
      }
      commit('setIndexItemsCount', result.data.count);
      commit('setIndexItems', resources);
    } catch (error) {
      console.log('error', error);
    }
    // empty
    commit('setResourceIndexState', 'loaded');
    commit("setInfiniteScroll", true);
  },
  resetMyResource({ commit }) {
    commit("resetMyResource");
  },
  async updateToken(store, { token }) {
    return axios.post('update_fcm_token', { token });
  },
  async saveResourceImage(store, { blob }) {
    store.state.resourceDraft.pendingUploads++;

    let firebase = this._vm.firebase;
    const currentUserId = await store.dispatch("loggedInUserId");

    let storageRef = firebase.storage().ref();
    let userImageRef = storageRef.child(`resources/${currentUserId}/${new Date().getTime()}.jpg`);

    if (blob && blob.size !== undefined) {
      const userImageSnapshot = await userImageRef.put(blob);
      const url = await userImageSnapshot.ref.getDownloadURL();
      store.state.resourceDraft.images.push({ storage: url });
      store.state.resourceDraft.pendingUploads--;
    }
  },
  async saveUserImage(store, { blob }) {
    let firebase = this._vm.firebase;
    const currentUserId = await store.dispatch("loggedInUserId");
    let storageRef = firebase.storage().ref();
    let userImageRef = storageRef.child(`users/${currentUserId}/photo.jpg`);
    let database = firebase.database();

    if (blob && blob.size !== undefined) {
      const userImageSnapshot = await userImageRef.put(blob);
      const image = userImageSnapshot.downloadURL;
      let ref = database.ref().child(`users/${currentUserId}`);
      await ref.update({ image: { storage: image } });
      return Promise.resolve({ image: { storage: image } });
    } else {
      try {
        await userImageRef.delete();
      } catch (e) {}
      let ref = database.ref().child(`users/${currentUserId}`);
      await ref.update({ image: null });
      return Promise.resolve(null);
    }
  },
  async saveUser(store, { user }) {
    const database = this._vm.firebase.database();
    let params = {
      name: user.name,
      language: user.language
    };
    const currentUserId = await store.dispatch("loggedInUserId");
    let ref = database.ref().child(`users/${currentUserId}`);
    await ref.update(params);
    return Promise.resolve(params);
  },
  async addToGroup(store, { group_id }) {
    const database = this._vm.firebase.database();
    let groupsRef = database.ref().child('groups').child(group_id);
    let snap = await groupsRef.once('value');
    if (snap.val()) {
      const group = snap.val();
      await this.dispatch('ADD_USER_TO_GROUP', { group_id });
      return Promise.resolve(group.name);
    } else {
      return Promise.reject();
    }
  },
  async ADD_USER_TO_GROUP(store, { group_id }) {
    const database = this._vm.firebase.database();
    const user_id = await store.dispatch("loggedInUserId");
    let userGroupsRef = database.ref().child('user_groups').child(`${group_id}|${user_id}`);

    await userGroupsRef.set({
      group_id,
      user_id,
      created_at: this._vm.firebase.database.ServerValue.TIMESTAMP
    });

    return Promise.resolve();
  },
  async getLastMessage(store, { destination }) {
    const database = this._vm.firebase.database();
    const currentUserId = await store.dispatch("loggedInUserId");
    return new Promise((resolve, reject) => {
      let ref = database.ref().child('chat').child(currentUserId).child(destination).child('messages').limitToLast(1);
      ref.once("value", (snap) => {
        resolve(snap.val()[Object.keys(snap.val())[0]]);
      });
    });
  },
  async watchMyMessage({ commit }, { id }) {
    const database = this._vm.firebase.database();
    const currentUserId = await store.dispatch("loggedInUserId");
    const ref = database.ref().child('chat').child(currentUserId).child(id).child('messages');
    ref.on("value", snap => commit("setMyMessage", snap.val()));
  },
  async createGroup(store, group) {
    group.contacts = group.contacts.map(contact => ({ name: contact.name, phone: contact.phone }));
    const groupToAdd = Object.assign({ id: `group::${store.rootState.user.getUser().id}${new Date().getTime()}` }, group);
    const addedGroup = await axios.post('group', groupToAdd);
    store.commit('addSingleGroup', addedGroup);
    return Promise.resolve();
  },
  async getContacts(store) {
    const response = await axios.get(`group`);
    store.commit('setContacts', response.data.contacts);
    return Promise.resolve();
  },
  async getUser({}, { id }) {
    const database = this._vm.firebase.database();
    const ref = database.ref().child('users').child(id);
    const snap = await ref.once("value");
    return User.fromSnapshot(snap);
  },
  onGetUser({ commit }, { id }) {
    const database = this._vm.firebase.database();
    let ref = database.ref().child('users').child(id);
    ref.on("value", (snap) => {
      commit("setUser", User.fromSnapshot(snap));
    });
  },
};

const mutations = {
  ...globalMutations,
  ...indexMutations,
  ...userMutations,
  ...resourceMutations,
  ...authMutations,
  ...groupMutations,
  ...chatMutations,
  ...userMutations,
  ...snackbarMutations,
  ...shareMutations,
  setCurrentUser(state, currentUser) {
    state.currentUser = currentUser;
  },
  setMyMessage(state, messages) {
    state.myMessages = messages;
  },
  profileStateLoading(state) {
    state.profileState = 'loading';
  },
  resetMyGroup(state) {
    state.myGroupsIds = [];
    state.myGroups = {};
  },
  setMyGroup(state, group) {
    if (!state.myGroupsIds.includes(group.id)) {
      state.myGroupsIds.push(group.id);
      state.myGroups[group.id] = group;
    }
  },
  setInfiniteScroll(state, bool) {
    state.infiniteScrollEnabled = bool;
  },
  showLogin(state, login) {
    state.showLogin = login;
  },
  setLoggedInUser(state, user) {
    state.loggedInUser = user;
  },
  setIndexItemsCount(state, num) {
    state.indexItemsCount = num;
  },
  setIndexItems(state, items) {
    state.indexItems = state.indexItems.concat(items);
    state.resourceIndexState = 'loaded';
  },
  clearCategories(state) {
    state.indexCategoriesFilters = [];
    localStorage.setItem('categories', JSON.stringify([]));
  },
  clearGroups(state) {
    state.indexGroupsFilters = [];
    localStorage.setItem('groups', JSON.stringify([]));
  },
  SET_LANG(state, locale) {
    if (state.locales.indexOf(locale) !== -1) {
      state.locale = locale;
    }
  },
  RESET_RESOURCE_AND_SCROLL_STATE(state) {
    state.indexItems = [];
    state.indexItemsPosition = 0;
    state.resourceIndexState = 'idle';
  },
  setResourceIndexState(state, name) {
    state.resourceIndexState = name;
  },
  setUser(state, user) {
    state.currentUser = user;
  }
};

const getters = {
  ...globalGetters,
  ...indexGetters,
  ...chatGetters,
  ...groupGetters,
  ...userGetters,
  ...resourceGetters,
  ...authGetters,
  ...userGetters,
  ...snackbarGetters,
  ...shareGetters,
  getMessage: state => state.message,
  getCountryCode: state => state.countryCode,
  indexItemsCount: state => state.indexItemsCount,
  infiniteScrollEnabled: state => state.infiniteScrollEnabled,
  getCurrentUserId: state => state.currentUser.uid,
  getCurrentUser: state => state.currentUser,
  indexItems: (state) => {
    return state.indexItems.map(resource => new Resource(resource));
  },
  indexCategoriesFilters: state => state.indexCategoriesFilters,
  selectedFiltersLocalized: (state) => {
    let filters = [];
    filters = filters.concat(
      state.indexGroupsFilters.map(group => group.name)
    );
    filters = filters.concat(
      state.indexCategoriesFilters.map(c => "cat_" + c)
    );
    return filters;
  },
  indexPage: state => (state.indexItems === 0) ? 1 : Math.ceil(state.indexItems.length / 15) + 1,
};

const store = new Vuex.Store({
  state,
  getters,
  actions,
  mutations
});

export default store;
